import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  Calendar,
  User,
  MapPin,
  Flag,
  Clock,
  Users,
  Edit3,
  Trash2,
  Globe,
  Lock,
  Mail,
  ExternalLink,
} from "lucide-react";
import { useEvent, useDeleteEvent } from "@/hooks/useEvents";
import { useAuth } from "@/hooks/useAuth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { TrackTypeBadge } from "@/components/tracks/TrackTypeBadge";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { ImageModal } from "@/components/ui/ImageModal";
import PageLoading from "@/components/common/PageLoading";
import PageErrorState from "@/components/common/PageErrorState";
import { formatUserName } from "@/utils/userName";

// Helper function to format event type for display
const formatEventType = (eventType: string): string => {
  return eventType
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t, i18n } = useTranslation("events");

  const fromSearch = (location.state as { fromSearch?: string } | null)?.fromSearch ?? "";
  const eventsListUrl = `/events${fromSearch.startsWith("?") ? fromSearch : ""}`;

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const { data: event, isLoading, error } = useEvent(eventId!);
  usePageMeta({ title: event?.name, description: event?.description });
  const deleteEventMutation = useDeleteEvent();

  if (!eventId) {
    return (
 <div className="page-container">
 <div className="text-center py-12">
 <p className="text-lg text-red-600">{t("detail.invalidId")}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="page-container">
        <PageLoading />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="page-container">
        <PageErrorState
          icon={Flag}
          message={t("detail.failedToLoad")}
          details={t("detail.noAccessMessage")}
          retryLabel={t("detail.backToEvents")}
          onRetry={() => navigate(eventsListUrl)}
        />
      </div>
    );
  }

  const canEdit = user?.id === event.userId;
  const canDelete = user?.id === event.userId;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleDelete = async () => {
    try {
      await deleteEventMutation.mutateAsync(eventId);
      navigate(eventsListUrl);
    } catch {
      // useDeleteEvent owns the error toast; stay on the detail page.
    }
  };

  const handleTrackClick = () => {
    navigate(`/tracks/${event.track.id}`);
  };

  const getVisibilityIcon = () => {
    switch (event.visibility) {
      case "public":
 return <Globe className="h-5 w-5 text-secondary-400 flex-shrink-0" />;
      case "invitation_only":
 return <Mail className="h-5 w-5 text-secondary-400 flex-shrink-0" />;
      default:
 return <Lock className="h-5 w-5 text-secondary-400 flex-shrink-0" />;
    }
  };

  const getVisibilityLabel = () => {
    switch (event.visibility) {
      case "public":
        return t("visibility.public");
      case "invitation_only":
        return t("visibility.invitationOnly");
      default:
        return t("visibility.private");
    }
  };

  return (
 <div className="page-container w-full overflow-hidden">
      {/* Event Cover Image */}
      {event.coverImageUrl && (
 <div className="mb-3 sm:mb-8 mx-[calc(var(--shell-gutter-x)*-1)]">
          <button
            onClick={() => setIsImageModalOpen(true)}
 className="w-full relative group"
            title={t("events:detail.clickToViewFullImage")}
          >
            <img
              src={event.coverImageUrl}
              alt={event.name}
              className="w-full h-64 sm:h-96 object-cover transition-opacity group-hover:opacity-90"
            />
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black bg-opacity-30">
              <div className="px-4 py-2 bg-white bg-opacity-90 rounded-lg text-sm font-medium text-secondary-800">
                {t("events:detail.clickToViewFullImage")}
              </div>
            </div>
          </button>
        </div>
      )}

      {/* Header */}
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 w-full">
 <div className="flex items-center gap-3 min-w-0 w-full sm:w-auto">
          <button
            onClick={() => navigate(eventsListUrl)}
            aria-label={t("detail.backToEvents")}
 className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-lg transition-colors flex-shrink-0"
          >
 <ArrowLeft className="h-5 w-5" />
          </button>
 <div className="min-w-0 flex-1">
 <h1 className="page-title break-words leading-tight">
              {event.name}
            </h1>
 <p className="page-subtitle">
              {t("detail.title")}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        {canEdit && (
 <div className="flex gap-2 self-start sm:self-center flex-shrink-0">
            <button
              onClick={() => navigate(`/events/${eventId}/edit`)}
 className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
            >
 <Edit3 className="h-4 w-4" />
              <span>{t("detail.edit")}</span>
            </button>
            {canDelete && (
              <button
                onClick={() => setShowDeleteConfirm(true)}
 className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
              >
 <Trash2 className="h-4 w-4" />
                <span>{t("detail.delete")}</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Event Info Card */}
 <div className="bg-white rounded-lg border border-secondary-200 mb-8">
 <div className="p-4 sm:p-6 border-b border-secondary-100">
 <h2 className="text-lg font-semibold text-secondary-900">
            {t("detail.eventInformation")}
          </h2>
        </div>
 <div className="p-4 sm:p-6">
 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
 <div className="space-y-4">
 <div className="flex items-center space-x-3">
 <User className="h-5 w-5 text-secondary-400 flex-shrink-0" />
                <div>
 <div className="text-sm text-secondary-500">
                    {t("fields.organizer")}
                  </div>
 <div className="font-medium text-secondary-900">
                    {formatUserName(event.user)}
                  </div>
                </div>
              </div>

 <div className="flex items-center space-x-3">
 <MapPin className="h-5 w-5 text-secondary-400 flex-shrink-0" />
                <div>
 <div className="text-sm text-secondary-500">
                    {t("fields.track")}
                  </div>
                  <div className="font-medium text-secondary-900 flex items-center gap-2">
                    <span
                      className="cursor-pointer hover:text-primary-600 hover:underline transition-colors"
                      onClick={handleTrackClick}
                    >
                      {event.track.name}
                    </span>
                    <TrackTypeBadge type={event.track.type} />
                  </div>
                </div>
              </div>

 <div className="flex items-center space-x-3">
 <Flag className="h-5 w-5 text-secondary-400 flex-shrink-0" />
                <div>
 <div className="text-sm text-secondary-500">
                    {t("fields.eventType")}
                  </div>
 <div className="font-medium text-secondary-900">
                    {formatEventType(event.eventType)}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column */}
 <div className="space-y-4">
 <div className="flex items-center space-x-3">
 <Calendar className="h-5 w-5 text-secondary-400 flex-shrink-0" />
                <div>
 <div className="text-sm text-secondary-500">
                    {t("fields.startDate")}
                  </div>
 <div className="font-medium text-secondary-900">
                    {formatDate(event.startDate)}
                  </div>
                </div>
              </div>

 <div className="flex items-center space-x-3">
 <Clock className="h-5 w-5 text-secondary-400 flex-shrink-0" />
                <div>
 <div className="text-sm text-secondary-500">
                    {t("fields.endDate")}
                  </div>
 <div className="font-medium text-secondary-900">
                    {formatDate(event.endDate)}
                  </div>
                </div>
              </div>

 <div className="flex items-center space-x-3">
                {getVisibilityIcon()}
                <div>
 <div className="text-sm text-secondary-500">
                    {t("fields.visibility")}
                  </div>
 <div className="font-medium text-secondary-900">
                    {getVisibilityLabel()}
                  </div>
                </div>
              </div>

              {event.maxParticipants && (
 <div className="flex items-center space-x-3">
 <Users className="h-5 w-5 text-secondary-400 flex-shrink-0" />
                  <div>
 <div className="text-sm text-secondary-500">
                      {t("fields.maxParticipants")}
                    </div>
 <div className="font-medium text-secondary-900">
                      {t("fields.participantCount", {
                        count: event.maxParticipants,
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {event.description && (
 <div className="mt-6 pt-6 border-t border-secondary-100">
 <h3 className="font-semibold text-secondary-700 mb-2">
                {t("fields.description")}
              </h3>
 <p className="text-secondary-800 whitespace-pre-wrap">
                {event.description}
              </p>
            </div>
          )}

          {(event.descriptionUrl || event.applicationUrl) && (
 <div className="mt-6 pt-6 border-t border-secondary-100">
              <h3 className="font-semibold text-secondary-700 mb-3">{t("events:detail.links")}</h3>
 <div className="space-y-2">
                {event.descriptionUrl && (
                  <a
                    href={event.descriptionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
 className="flex items-center gap-2 text-primary-600 hover:text-primary-800 hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>{t("events:detail.eventUrl")}</span>
                  </a>
                )}
                {event.applicationUrl && (
                  <a
                    href={event.applicationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
 className="flex items-center gap-2 text-primary-600 hover:text-primary-800 hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>{t("events:detail.applicationUrl")}</span>
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sessions Card */}
 <div className="bg-white rounded-lg border border-secondary-200 mb-8">
 <div className="p-4 sm:p-6 border-b border-secondary-100">
 <div className="flex items-center justify-between">
 <h2 className="text-lg font-semibold text-secondary-900">
              {t("detail.sessionsCount", { count: event.sessions.length })}
            </h2>
          </div>
        </div>
 <div className="p-4 sm:p-6">
          {event.sessions.length === 0 ? (
 <div className="text-center py-8">
 <Clock className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
 <p className="text-secondary-500">{t("detail.noSessions")}</p>
 <p className="text-sm text-secondary-400 mt-2">
                {t("detail.noSessionsMessage")}
              </p>
            </div>
          ) : (
 <div className="space-y-3">
              {event.sessions.map((session) => (
                <div
                  key={session.id}
 className="bg-secondary-50 rounded-lg p-4 hover:bg-secondary-100 transition-colors cursor-pointer"
                  onClick={() => navigate(`/sessions/${session.id}`)}
                >
 <div className="flex justify-between items-start">
 <div className="flex-1 min-w-0">
 <h3 className="font-medium text-secondary-900 mb-1">
                        {session.title || t("detail.untitledSession")}
                      </h3>
 <div className="flex flex-wrap items-center gap-3 text-sm text-secondary-600">
 <div className="flex items-center gap-1">
 <User className="h-4 w-4" />
                          <span>
                            {formatUserName(session.user)}
                          </span>
                        </div>
 <div className="flex items-center gap-1">
 <Calendar className="h-4 w-4" />
                          <span>
                            {new Date(session.createdAt).toLocaleDateString(
                              i18n.language,
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
 <ArrowLeft className="h-5 w-5 text-secondary-400 transform rotate-180 flex-shrink-0 ml-2" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t("deleteConfirmation.title")}
        message={t("deleteConfirmation.message", { eventName: event.name })}
        confirmButtonText={t("deleteConfirmation.confirmButton")}
        confirmButtonColor="red"
        isLoading={deleteEventMutation.isPending}
      />

      <ImageModal
        isOpen={isImageModalOpen}
        onClose={() => setIsImageModalOpen(false)}
        imageUrl={event.coverImageUrl || ""}
        alt={event.name}
      />
    </div>
  );
}
